import { createSlice } from '@reduxjs/toolkit';
import { isArray, isEmpty } from 'lodash';

const initialState = {
  rowsList: [],
  columnSettingsList: [],
  loading: false,
  error: undefined,
  rowProps: [],
};

const agGridSlice = createSlice({
  name: 'agGrid',
  initialState,
  reducers: {
    clearError(state) {
      state.error = undefined;
    },
    actionStart(state) {
      state.loading = true;
      state.error = undefined;
    },
    actionError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    fetchRowsList(state, action) {
      const { data, inForm } = action.payload;
      const propName = inForm ? `${inForm}Rows` : 'rowsList';
      state[propName] = data;
      state.error = undefined;
      state.loading = false;
      state.rowProps = data?.[0] ? Object.keys(data?.[0]) : [];
    },
    fetchSettingsList(state, action) {
      const { data, inForm } = action.payload;
      const propName = inForm ? `${inForm}Columns` : 'columnSettingsList';
      state[propName] = data;
      state.error = undefined;
      state.loading = false;
    },
    clearRowsList(state, action) {
      const payload = action?.payload;
      const propName = payload ? `${payload}Rows` : 'rowsList';
      state[propName] = [];
      state.error = undefined;
      state.loading = false;
      state.rowProps = [];
    },
    clearSettingsList(state, action) {
      const payload = action?.payload;
      const propName = payload ? `${payload}Columns` : 'columnSettingsList';
      state[propName] = [];
      state.error = undefined;
      state.loading = false;
    },
    deleteRow(state, action) {
      const { data } = action.payload;
      state.rowsList = state.rowsList?.filter((r) => r.id !== data?.id);
      state.error = undefined;
      state.loading = false;
    },
    activateRow(state, action) {
      const { data, inForm } = action.payload;
      const propName = inForm ? `${inForm}Rows` : 'rowsList';
      if (Array.isArray(data)) {
        state[propName] = state[propName]?.map((r) => {
          const item = data?.find((item) => item.id === r.id);
          return item ? { ...r, ...item } : r;
        });
      } else {
        state[propName] = state[propName]?.map((r) =>
          r.id === data?.id ? { ...r, ...data } : r
        );
      }
      state.error = undefined;
      state.loading = false;
    },
    deactivateRow(state, action) {
      const { data, inForm } = action.payload;
      const propName = inForm ? `${inForm}Rows` : 'rowsList';
      if (Array.isArray(data)) {
        state[propName] = state[propName]?.map((r) => {
          const item = data?.find((item) => item.id === r.id);
          return item ? { ...r, ...item } : r;
        });
      } else {
        state[propName] = state[propName]?.map((r) =>
          r.id === data?.id ? { ...r, ...data } : r
        );
      }
      state.error = undefined;
      state.loading = false;
    },
    activateRowForModal(state, action) {
      const { data } = action.payload;
      if (Array.isArray(data)) {
        state.rowsList = state.rowsList?.map((r) => {
          const item = data?.find((item) => item.id === r.id);
          return item ? { ...r, ...item } : r;
        });
      } else {
        state.rowsList = state.rowsList?.map((r) =>
          r.id === data?.id ? { ...r, ...data } : r
        );
      }
      state.error = undefined;
      state.loading = false;
    },
    deactivateRowForModal(state, action) {
      if (Array.isArray(action.payload)) {
        state.rowsList = state.rowsList?.map((r) => {
          const item = action.payload?.find((item) => item.id === r.id);
          return item ? { ...r, ...item } : r;
        });
      } else {
        state.rowsList = state.rowsList?.map((r) =>
          r.id === action.payload?.id ? { ...r, ...action.payload } : r
        );
      }
      state.error = undefined;
      state.loading = false;
    },
    //TODO srediti ^^'
    createRow(state, action) {
      const payload = action?.payload;
      //Pronađi props-e, čiji je sadržaj niz i nije prazan jer imamo duplikate
      //i čiji je deo naziva zastupljen u aggrid-u
      const isPayloadArray = Array.isArray(payload);
      let rowsForInsert = isPayloadArray ? payload : [];
      if (!isPayloadArray) {
        const arrayProps = Object.entries(payload)?.filter((entry) => {
          const lastIndexForSubstring = entry[0].endsWith('List')
            ? entry[0].lastIndexOf('List')
            : entry[0]?.length - 1;
          const joinTableName = entry[0].substring(0, lastIndexForSubstring);
          return (
            isArray(entry[1]) &&
            !isEmpty(entry[1]) &&
            state.rowProps.find((r) => r.startsWith(joinTableName))
          );
        });
        if (isEmpty(arrayProps)) {
          rowsForInsert = [payload];
        }
        arrayProps?.forEach((arrayProp) => {
          const arrayPropData = arrayProp[1];
          const arrayPropKey = arrayProp[0];
          //Uzmi naziv join tabele
          //nazivi listi na back-u uvek startuju sa nazivom modela + 's'/'List'
          const lastIndexForSubstring = arrayPropKey.endsWith('List')
            ? arrayPropKey.lastIndexOf('List')
            : arrayPropKey?.length - 1;
          const joinTableName = arrayPropKey.substring(
            0,
            lastIndexForSubstring
          );
          //Pronađi nazive kolona iz aggrid-a čiji naziv startuje sa nazivom modela sa kojim je napravljen join
          const propsThatMakeDuplicates = state.rowProps.filter((r) =>
            r.startsWith(joinTableName)
          );
          //Uzmi nazive kolone + vrednost iz objekta join tabele, da bi znali koje kolone sadrži join tabela
          const arrayPropDataEntriesKeys = Object.keys(arrayPropData[0]);
          const originalJoinObjectKeysForUpdate = [];
          //Prođi kroz join kolone redom, npr. ako je prioritet i naziv role zastupljen u aggrid-u (roleName, rolePriority)
          propsThatMakeDuplicates.forEach((propThatMakesDuplicate) => {
            //uzmi naziv konkretnog atributa iz join tabele čija vrednost se koristi npr. digitalRecordTypeName -> name, roleName -> roleName
            //prvo proveri podudara li se već neki prop, a ako ne, onda iseci i prilagodi string
            const fullAttributeName = arrayPropDataEntriesKeys?.find(
              (a) => a === propThatMakesDuplicate
            );
            let attributeName;
            if (fullAttributeName) {
              attributeName = fullAttributeName;
            } else {
              const locatedAttribute = propThatMakesDuplicate?.substring(
                propThatMakesDuplicate?.indexOf(joinTableName) + 1
              );
              attributeName = locatedAttribute.replace(
                0,
                locatedAttribute[0].toLowerCase()
              );
            }
            //ubaci aggrid naziv kolone sa nazivom kolone u modelu
            originalJoinObjectKeysForUpdate.push({
              aggridName: propThatMakesDuplicate,
              originalName: attributeName,
            });
          });
          //Definiši ažurirani sadržaj - tj. sadržaj koji će se ubaciti umesto starog
          rowsForInsert = rowsForInsert.concat(
            arrayPropData?.map((data) => {
              const mainContent = { ...payload };
              delete mainContent[arrayPropKey]; //brišemo listu koja pravi join - ne moramo opterećivati stranu sa skrivenim nizovima :D
              originalJoinObjectKeysForUpdate.forEach((key) => {
                mainContent[key.aggridName] = data[key.originalName];
              });
              return mainContent;
            }) || []
          );
        });
      }
      state.rowsList = rowsForInsert?.concat(state.rowsList);
      state.error = undefined;
      state.loading = false;
    },
    createRowForModal(state, action) {
      const newData = Array.isArray(action.payload.data)
        ? action.payload.data
        : [action.payload.data];
      const listName = `${action.payload.type}Rows`;
      state[listName] = state[listName]
        ? newData?.concat(state[listName])
        : newData;
      state.error = undefined;
      state.loading = false;
    },
    updateRowForModal(state, action) {
      const listName = `${action.payload.type}Rows`;
      const data = action.payload.data;
      const isPayloadArray = Array.isArray(data);
      if (isPayloadArray) {
        state[listName] = state[listName].map((entity) => {
          const updatedObject = data.find((object) => object.id === entity.id);
          return updatedObject || entity;
        });
      } else {
        state[listName] = state[listName].map((entity) =>
          entity.id === action.payload.data.id ? action.payload.data : entity
        );
      }
      state.error = undefined;
      state.loading = false;
    },
    //props 'targetProp' sluzi da u slucaju hocemo da obrisemo neki entitet
    //ali ne poredeci po id-u vec po nekom drugom atributu
    deleteRowForModal(state, action) {
      const listName = `${action.payload.type}Rows`;
      state[listName] = state[listName].filter(
        (entity) =>
          entity[action.payload.targetProp || 'id'] !== action.payload.id
      );
      state.error = undefined;
      state.loading = false;
    },
    //TODO srediti ^^'
    updateRow(state, action) {
      const payload = action?.payload;
      const isPayloadArray = Array.isArray(payload);
      const payloadData = isPayloadArray ? payload[0] : payload;
      //Pronađi sve redove sa istim id-em u aggrid-u
      const dataWithSameId = state?.rowsList?.filter(
        (r) => r.id === payloadData?.id
      );
      let rowsForInsert = isPayloadArray ? payload : [];
      if (!isPayloadArray) {
        //Pronađi props-e, čiji je sadržaj niz i nije prazan jer imamo duplikate
        //i čiji je deo naziva zastupljen u aggrid-u
        const arrayProps = Object.entries(payload)?.filter((entry) => {
          const lastIndexForSubstring = entry[0].endsWith('List')
            ? entry[0].lastIndexOf('List')
            : entry[0]?.length - 1;
          const joinTableName = entry[0].substring(0, lastIndexForSubstring);
          return (
            isArray(entry[1]) &&
            !isEmpty(entry[1]) &&
            state.rowProps.find((r) => r.startsWith(joinTableName))
          );
        });
        if (isEmpty(arrayProps)) {
          rowsForInsert = [payload];
        }
        arrayProps.forEach((arrayProp) => {
          const arrayPropData = arrayProp[1];
          const arrayPropKey = arrayProp[0];
          //Uzmi naziv join tabele
          //nazivi listi na back-u uvek startuju sa nazivom modela + 's'/'List'
          const lastIndexForSubstring = arrayPropKey.endsWith('List')
            ? arrayPropKey.lastIndexOf('List')
            : arrayPropKey?.length - 1;
          const joinTableName = arrayPropKey.substring(
            0,
            lastIndexForSubstring
          );
          //Pronađi nazive kolona iz aggrid-a čiji naziv startuje sa nazivom modela sa kojim je napravljen join
          const propsThatMakeDuplicates = state.rowProps.filter((r) =>
            r.startsWith(joinTableName)
          );
          //Uzmi nazive kolone + vrednost iz objekta join tabele, da bi znali koje kolone sadrži join tabela
          const arrayPropDataEntriesKeys = Object.keys(arrayPropData[0]);
          const originalJoinObjectKeysForUpdate = [];
          //Prođi kroz join kolone redom, npr. ako je prioritet i naziv role zastupljen u aggrid-u (roleName, rolePriority)
          propsThatMakeDuplicates.forEach((propThatMakesDuplicate) => {
            //uzmi naziv konkretnog atributa iz join tabele čija vrednost se koristi npr. digitalRecordTypeName -> name, roleName -> roleName
            //prvo proveri podudara li se već neki prop, a ako ne, onda iseci i prilagodi string
            const fullAttributeName = arrayPropDataEntriesKeys?.find(
              (a) => a === propThatMakesDuplicate
            );
            let attributeName;
            if (fullAttributeName) {
              attributeName = fullAttributeName;
            } else {
              const locatedAttribute = propThatMakesDuplicate?.substring(
                propThatMakesDuplicate?.indexOf(joinTableName) + 1
              );
              attributeName = locatedAttribute.replace(
                0,
                locatedAttribute[0].toLowerCase()
              );
            }
            //ubaci aggrid naziv kolone sa nazivom kolone u modelu
            originalJoinObjectKeysForUpdate.push({
              aggridName: propThatMakesDuplicate,
              originalName: attributeName,
            });
          });
          //Definiši ažurirani sadržaj - tj. sadržaj koji će se ubaciti umesto starog
          rowsForInsert = rowsForInsert.concat(
            arrayPropData?.map((data) => {
              const mainContent = { ...dataWithSameId[0], ...payload };
              delete mainContent[arrayPropKey]; //brišemo listu koja pravi join - ne moramo opterećivati stranu sa skrivenim nizovima :D
              originalJoinObjectKeysForUpdate.forEach((key) => {
                mainContent[key.aggridName] = data[key.originalName];
              });
              return mainContent;
            }) || []
          );
        });
      }
      if (!isEmpty(rowsForInsert)) {
        //Pronađi index prvog za zamenu sa novim vrednostima
        const indexOfFirst = state.rowsList.findIndex(
          (r) => r.id === payloadData.id
        );
        // obriši i na njihovo mesto ubaci ažurirane vrednosti
        state.rowsList.splice(
          indexOfFirst,
          dataWithSameId?.length,
          ...rowsForInsert
        );
      } else {
        state.rowsList = state.rowsList?.map((r) =>
          r.id === payload?.id ? { ...r, ...payload } : r
        );
      }
      state.error = undefined;
      state.loading = false;
    },
  },
});

export const agGridActions = agGridSlice.actions;

export default agGridSlice.reducer;
